import React, { useState, useCallback, FormEvent } from "react";

import { AlertBox, Button, Input, Label } from "@iwoca/orion";
import { Trans } from "@lingui/react/macro";
import { Link, useNavigate } from "react-router";

import greenCheckSVG from "../../../assets/checkmark.svg";
import { usePostApi } from "../../../fetchApi.hook";
import logger from "../../../utils/logger";
import {
  initiateForgotPassword,
  confirmNewPassword,
} from "../../authentication-cognito-forgot-password";
import styles from "../../CommonLogin.module.css";
import { LoadingSpinner } from "../../LoadingSpinner/LoadingSpinner";
import LoginContainer from "../../LoginContainer";
import { isValidEmail } from "../../LoginHelpers";
import { determineAuthFlow } from "../../utils/checkEmailExists";

type FlowState =
  | "initial"
  | "django-sent"
  | "cognito-verify"
  | "success"
  | "error";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isEmailInvalid, setIsEmailInvalid] = useState(false);
  const [flowState, setFlowState] = useState<FlowState>("initial");
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [cognitoError, setCognitoError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { status, hasError, isLoading, triggerFetch } = usePostApi({
    endpoint: "/api/lending/edge/password/reset/",
  });

  const handleForgotPassword = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      const isEmailValid = isValidEmail(email);
      if (!isEmailValid) {
        setIsEmailInvalid(true);
        return;
      }

      if (isEmailValid) {
        try {
          // Determine which auth system the email belongs to
          const authFlow = await determineAuthFlow(email);

          if (authFlow === "django") {
            // User exists in Django - use Django password reset
            console.log("Using Django password reset flow");
            const result = await triggerFetch({ data: { email } });

            // Django always returns 204 for security (even if user doesn't exist)
            // so we treat any 204 as success
            if (result && !result.hasError && result.status === 204) {
              setFlowState("django-sent");
            }
            // If there's an error, it will be shown via the existing error state
          } else {
            // User doesn't exist in Django - use Cognito password reset
            console.log("Using Cognito password reset flow");

            const cognitoResult = await initiateForgotPassword(email);
            if (cognitoResult.success) {
              // Cognito verification code sent - show verification form
              setFlowState("cognito-verify");
              setCognitoError("");
            } else {
              logger.error("Cognito password reset failed", {
                error: cognitoResult.error?.message,
                code: cognitoResult.error?.code,
              });
              setCognitoError(
                cognitoResult.error?.message ||
                  "Failed to send verification code",
              );
              setFlowState("error");
            }
          }
        } catch (error: any) {
          logger.error("Password reset flow determination failed", {
            error: error?.message || "Unknown error",
          });
          // On error, show generic error message
          setCognitoError("Failed to initiate password reset");
          setFlowState("error");
        }
      }
    },
    [email, triggerFetch],
  );

  const handleConfirmPassword = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      setCognitoError("");

      // Validate inputs
      if (!verificationCode.trim()) {
        setCognitoError("Please enter the verification code");
        return;
      }

      if (!newPassword) {
        setCognitoError("Please enter a new password");
        return;
      }

      if (newPassword !== confirmPassword) {
        setCognitoError("Passwords do not match");
        return;
      }

      // Validate password meets Cognito password policy requirements
      if (newPassword.length < 12) {
        setCognitoError("Password must be at least 12 characters long");
        return;
      }

      if (!/[a-z]/.test(newPassword)) {
        setCognitoError("Password must contain at least one lowercase letter");
        return;
      }

      if (!/[A-Z]/.test(newPassword)) {
        setCognitoError("Password must contain at least one uppercase letter");
        return;
      }

      if (!/[0-9]/.test(newPassword)) {
        setCognitoError("Password must contain at least one number");
        return;
      }

      if (!/[^a-zA-Z0-9]/.test(newPassword)) {
        setCognitoError("Password must contain at least one symbol");
        return;
      }

      setIsSubmitting(true);

      try {
        const result = await confirmNewPassword(
          email,
          verificationCode,
          newPassword,
        );

        if (result.success) {
          setFlowState("success");
        } else {
          setCognitoError(result.error?.message || "Failed to reset password");
        }
      } catch (error: any) {
        setCognitoError(error.message || "An unexpected error occurred");
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, verificationCode, newPassword, confirmPassword],
  );

  const handleBackToEmail = () => {
    setFlowState("initial");
    setVerificationCode("");
    setNewPassword("");
    setConfirmPassword("");
    setCognitoError("");
  };

  // Show verification code + new password form for Cognito users
  if (flowState === "cognito-verify") {
    return (
      <LoginContainer>
        <form
          className={styles.loginWithPassword}
          onSubmit={handleConfirmPassword}
        >
          <h2>
            <Trans>Reset my password</Trans>
          </h2>
          <p className={styles.instructions}>
            <Trans>
              We've sent a verification code to {email}. Enter it below along
              with your new password.
            </Trans>
          </p>

          <Label htmlFor="code-input" className={styles.loginLabel}>
            <Trans>Verification Code</Trans>
          </Label>
          <Input
            data-testid="code-input"
            placeholder="123456"
            id="code-input"
            value={verificationCode}
            onChange={(event) => {
              setCognitoError("");
              setVerificationCode(event.target.value);
            }}
            autoComplete="off"
          />

          <Label htmlFor="new-password-input" className={styles.loginLabel}>
            <Trans>New Password</Trans>
          </Label>
          <Input
            className="fs-exclude"
            data-testid="new-password-input"
            type="password"
            id="new-password-input"
            value={newPassword}
            onChange={(event) => {
              setCognitoError("");
              setNewPassword(event.target.value);
            }}
            autoComplete="new-password"
          />
          <p
            className={styles.instructions}
            style={{ marginTop: "8px", fontSize: "14px" }}
          >
            <Trans>
              Password must be at least 12 characters and include uppercase,
              lowercase, numbers, and symbols
            </Trans>
          </p>

          <Label htmlFor="confirm-password-input" className={styles.loginLabel}>
            <Trans>Confirm your new password</Trans>
          </Label>
          <Input
            className="fs-exclude"
            data-testid="confirm-password-input"
            type="password"
            id="confirm-password-input"
            value={confirmPassword}
            onChange={(event) => {
              setCognitoError("");
              setConfirmPassword(event.target.value);
            }}
            autoComplete="new-password"
          />

          {cognitoError ? (
            <AlertBox
              variant="failure"
              className={styles.errorBox}
              data-testid="cognito-error-message"
            >
              {cognitoError}
            </AlertBox>
          ) : null}

          <Button
            variant="primary"
            disabled={isSubmitting}
            type="submit"
            className={styles.submitButtonFull}
          >
            {isSubmitting ? (
              <LoadingSpinner />
            ) : (
              <Trans>Reset my password</Trans>
            )}
          </Button>

          <button
            type="button"
            onClick={handleBackToEmail}
            className={styles.linkButton}
            style={{
              marginTop: "20px",
              background: "none",
              border: "none",
              cursor: "pointer",
            }}
          >
            <Trans>Back to email entry</Trans>
          </button>
        </form>
      </LoginContainer>
    );
  }

  // Show success message for Cognito password reset
  if (flowState === "success") {
    return (
      <LoginContainer>
        <div className={styles.loginWithPassword}>
          <AlertBox variant="success" className={styles.goodBox}>
            <img src={greenCheckSVG} className={styles.checkmark} alt="" />
            <div>
              <h2 style={{ marginBottom: "10px" }}>
                <Trans>Password Reset Successfully</Trans>
              </h2>
              <div>
                <Trans>You can now log in with your new password.</Trans>
              </div>
            </div>
          </AlertBox>
          <Button
            variant="primary"
            onClick={() => navigate("/login/password")}
            className={styles.submitButtonFull}
            style={{ marginTop: "20px" }}
          >
            <Trans>Go to Login</Trans>
          </Button>
        </div>
      </LoginContainer>
    );
  }

  // Show error state for Cognito
  if (flowState === "error") {
    return (
      <LoginContainer>
        <div className={styles.loginWithPassword}>
          <AlertBox variant="failure" className={styles.errorBox}>
            <Trans>
              Sorry about that; we're looking into it. Try again later?
            </Trans>
            {cognitoError && (
              <p style={{ marginTop: "10px" }}>{cognitoError}</p>
            )}
          </AlertBox>
          <Button
            variant="primary"
            onClick={handleBackToEmail}
            className={styles.submitButtonFull}
            style={{ marginTop: "20px" }}
          >
            <Trans>Try Again</Trans>
          </Button>
        </div>
      </LoginContainer>
    );
  }

  // Initial state - email entry form
  return (
    <LoginContainer>
      <form
        className={styles.loginWithPassword}
        onSubmit={handleForgotPassword}
      >
        <Label htmlFor="email-input" className={styles.loginLabel}>
          <Trans>Email</Trans>
        </Label>
        <Input
          className="fs-exclude"
          data-testid="email-input"
          placeholder="name@provider.com"
          id="email-input"
          onChange={(event) => {
            setIsEmailInvalid(false);
            setEmail(event.target.value);
          }}
          onBlur={() => {
            if (!isValidEmail(email)) {
              setIsEmailInvalid(true);
            }
          }}
        />
        {isEmailInvalid ? (
          <AlertBox
            variant="failure"
            className={styles.errorBox}
            data-testid="error-message"
          >
            <Trans>Are you sure? This is not a valid email address</Trans>
          </AlertBox>
        ) : null}

        <p className={styles.instructions}>
          <Trans>
            Enter your email and we will send a link to reset your password.
          </Trans>
        </p>

        <Button
          variant="primary"
          disabled={isEmailInvalid || isLoading}
          type="submit"
          className={styles.submitButtonFull}
        >
          {isLoading ? (
            <LoadingSpinner />
          ) : !hasError && status === 204 ? (
            <Trans>Retry</Trans>
          ) : (
            <Trans>Reset my password</Trans>
          )}
        </Button>

        {!hasError && status === 204 ? (
          <AlertBox
            variant="success"
            className={styles.goodBox}
            data-testid="error-message"
          >
            <img src={greenCheckSVG} className={styles.checkmark} alt="" />
            <Trans>Check your email inbox; we've sent a link</Trans>
          </AlertBox>
        ) : null}

        {hasError && status !== 204 ? (
          <AlertBox variant="failure" data-testid="error-message">
            <Trans>Sorry, something’s gone wrong. Try again later?</Trans>
          </AlertBox>
        ) : null}

        <div className={styles.border}></div>

        <div className={styles.linkCopy} data-testid="link-to-password">
          <Trans>
            Or{" "}
            <Link className={styles.linkButton} to="/login/passwordless/email">
              email me a login link
            </Link>{" "}
            instead.
          </Trans>
        </div>
      </form>
    </LoginContainer>
  );
};

export default ForgotPassword;
